import React, { useState, useEffect } from 'react';
import { addOrder } from '../../../firebase/services';
import { auth } from '../../../firebase/config';
import { FaTrash } from 'react-icons/fa';
import TranslationButton from '../../ui/TranslationButton/TranslationButton';
import styles from './MarketerOrderForm.module.css';

const MarketerOrderForm = ({ marketerProducts }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [clientData, setClientData] = useState({
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    clientAddress: {
      street: '',
      city: '',
      country: '',
      postalCode: ''
    },
    clientNotes: ''
  });
  // Add state for total commission
  const [orderCommission, setOrderCommission] = useState('');
  
  // Add state for translated data
  const [translatedData, setTranslatedData] = useState({
    clientName: { ar: '', en: '', tr: '' },
    clientNotes: { ar: '', en: '', tr: '' }
  });

  useEffect(() => {
    console.log('MarketerOrderForm received products:', marketerProducts);
  }, [marketerProducts]);

  const handleClientDataChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('clientAddress.')) {
      const addressField = name.split('.')[1];
      setClientData(prev => ({
        ...prev,
        clientAddress: {
          ...prev.clientAddress,
          [addressField]: value
        }
      }));
    } else {
      setClientData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Update translated data for fields that can be translated
    if (['clientName', 'clientNotes'].includes(name)) {
      setTranslatedData(prev => ({
        ...prev,
        [name]: {
          ...prev[name],
          ar: value
        }
      }));
    }
  };

  const handleAddItem = (product) => {
    // Check if product is already in the selected items
    const existingItemIndex = selectedItems.findIndex(item => item.id === product.id);
    
    if (existingItemIndex !== -1) {
      // Update quantity if product already exists
      const updatedItems = [...selectedItems];
      updatedItems[existingItemIndex].quantity += 1;
      setSelectedItems(updatedItems);
    } else {
      // Add new product to selected items
      setSelectedItems(prev => [
        ...prev,
        {
          id: product.id,
          productId: product.productId || product.id,
          name: product.name,
          quantity: 1,
          basePrice: product.basePrice || product.price || 0,
          price: product.isOffer ? product.offerPrice : (product.basePrice || product.price || 0),
          finalPrice: product.isOffer ? product.offerPrice : (product.promotionalPrice || product.price || 0),
          marketerId: product.marketerId,
          imageUrl: product.image || product.imageUrl,
          isOffer: product.isOffer,
          offerPrice: product.isOffer ? product.offerPrice : null
        }
      ]);
    }
  };

  const handleRemoveItem = (productId) => {
    console.log('Removing item with ID:', productId);
    setSelectedItems(prev => {
      const filtered = prev.filter(item => item.id !== productId);
      console.log('Updated selected items:', filtered);
      return filtered;
    });
  };

  const handleQuantityChange = (productId, quantity) => {
    const updatedItems = selectedItems.map(item => 
      item.id === productId ? { ...item, quantity: parseInt(quantity) || 1 } : item
    );
    setSelectedItems(updatedItems);
  };

  const calculateTotal = () => {
    return selectedItems.reduce((total, item) => 
      total + (item.finalPrice * item.quantity), 0);
  };

  // Handle commission input change
  const handleCommissionChange = (e) => {
    // Only allow numbers and decimal point
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setOrderCommission(value);
  };

  // Handle translation for a specific field
  const handleTranslateField = (fieldName, translations) => {
    setTranslatedData(prev => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        en: translations.en,
        tr: translations.tr
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Parse the commission as a number
    const parsedCommission = parseFloat(orderCommission) || 0;

    const orderData = {
      items: selectedItems,
      totalAmount: calculateTotal(),
      totalCommission: parsedCommission, // Use the manually entered commission
      clientName: clientData.clientName,
      clientPhone: clientData.clientPhone,
      clientEmail: clientData.clientEmail,
      clientAddress: clientData.clientAddress,
      clientNotes: clientData.clientNotes,
      status: 'pending',
      marketerId: auth.currentUser?.uid, // Ensure marketerId is attached to the order
      // Add translated data
      translations: translatedData
    };

    const result = await addOrder(orderData);
    if (result.success) {
      alert(`Order #${result.orderNumber} created successfully!`);
      // Reset form after successful submission
      setSelectedItems([]);
      setOrderCommission('');
      setClientData({
        clientName: '',
        clientPhone: '',
        clientEmail: '',
        clientAddress: { street: '', city: '', country: '', postalCode: '' },
        clientNotes: ''
      });
      setTranslatedData({
        clientName: { ar: '', en: '', tr: '' },
        clientNotes: { ar: '', en: '', tr: '' }
      });
    } else {
      alert(`Failed to create order: ${result.error}`);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.marketerOrderForm}>
      <h3>Create New Order</h3>

      {/* Client Information */}
      <div className={styles.clientInfoSection}>
        <h4>Client Information</h4>
        <div className={styles.formGroup}>
          <label>Client Name*</label>
          <div className={styles.inputWithTranslation}>
            <input
              type="text"
              name="clientName"
              value={clientData.clientName}
              onChange={handleClientDataChange}
              placeholder="Full Name"
              required
            />
            <TranslationButton
              text={clientData.clientName}
              onTranslate={(translations) => handleTranslateField('clientName', translations)}
              size="small"
            />
          </div>
          {translatedData.clientName.en && (
            <div className={styles.translationResults}>
              <div className={styles.translationResult}>
                <span className={styles.translationLabel}>EN:</span>
                <span className={styles.translationText}>{translatedData.clientName.en}</span>
              </div>
              <div className={styles.translationResult}>
                <span className={styles.translationLabel}>TR:</span>
                <span className={styles.translationText}>{translatedData.clientName.tr}</span>
              </div>
            </div>
          )}
        </div>
        
        <div className={styles.formGroup}>
          <label>Phone Number*</label>
          <input
            type="tel"
            name="clientPhone"
            value={clientData.clientPhone}
            onChange={handleClientDataChange}
            placeholder="Phone Number"
            required
          />
        </div>
        
        <div className={styles.formGroup}>
          <label>Email</label>
          <input
            type="email"
            name="clientEmail"
            value={clientData.clientEmail}
            onChange={handleClientDataChange}
            placeholder="Email (optional)"
          />
        </div>
        
        <h5>Address</h5>
        <div className={styles.formGroup}>
          <input
            type="text"
            name="clientAddress.street"
            value={clientData.clientAddress.street}
            onChange={handleClientDataChange}
            placeholder="Street Address"
          />
        </div>
        
        <div className={styles.addressGrid}>
          <div className={styles.formGroup}>
            <input
              type="text"
              name="clientAddress.city"
              value={clientData.clientAddress.city}
              onChange={handleClientDataChange}
              placeholder="City"
            />
          </div>
          
          <div className={styles.formGroup}>
            <input
              type="text"
              name="clientAddress.country"
              value={clientData.clientAddress.country}
              onChange={handleClientDataChange}
              placeholder="Country"
            />
          </div>
          
          <div className={styles.formGroup}>
            <input
              type="text"
              name="clientAddress.postalCode"
              value={clientData.clientAddress.postalCode}
              onChange={handleClientDataChange}
              placeholder="Postal Code"
            />
          </div>
        </div>
        
        <div className={styles.formGroup}>
          <label>Additional Notes</label>
          <div className={styles.inputWithTranslation}>
            <textarea
              name="clientNotes"
              value={clientData.clientNotes}
              onChange={handleClientDataChange}
              placeholder="Additional Notes"
            />
            <TranslationButton
              text={clientData.clientNotes}
              onTranslate={(translations) => handleTranslateField('clientNotes', translations)}
              size="small"
            />
          </div>
          {translatedData.clientNotes.en && (
            <div className={styles.translationResults}>
              <div className={styles.translationResult}>
                <span className={styles.translationLabel}>EN:</span>
                <span className={styles.translationText}>{translatedData.clientNotes.en}</span>
              </div>
              <div className={styles.translationResult}>
                <span className={styles.translationLabel}>TR:</span>
                <span className={styles.translationText}>{translatedData.clientNotes.tr}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Product Selection */}
      <div className={styles.productSelectionSection}>
        <h4>Select Products</h4>
        {marketerProducts && marketerProducts.length > 0 ? (
          <div className={styles.productsGrid}>
            {marketerProducts.map(product => (
              <div key={product.id} className={styles.productItem}>
                <div className={styles.productImage}>
                  <img src={product.image || product.imageUrl || 'https://via.placeholder.com/150'} 
                       alt={product.name || 'Product'} />
                </div>
                <div className={styles.productDetails}>
                  <h5>{product.name || 'Unnamed Product'}</h5>
                  <p className={styles.productPrice}>
                    ${(product.promotionalPrice || product.price || 0).toFixed(2)}
                  </p>
                  <button 
                    type="button" 
                    className={styles.addButton}
                    onClick={() => handleAddItem(product)}
                  >
                    Add to Order
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.noProducts}>
            <p>No products available. Please add products in the "My Products" tab first.</p>
          </div>
        )}
      </div>

      {/* Selected Items */}
      {selectedItems.length > 0 && (
        <div className={styles.selectedItemsSection}>
          <h4>Selected Items</h4>
          <div className={styles.selectedItemsList}>
            {selectedItems.map((item, index) => (
              <div key={index} className={styles.selectedItem}>
                <div className={styles.itemDetails}>
                  <p className={styles.itemName}>{item.name}</p>
                  <p className={styles.itemPrice}>${item.finalPrice} each</p>
                </div>
                <div className={styles.itemControls}>
                  <div className={styles.quantityControl}>
                    <label>Qty:</label>
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                    />
                  </div>
                  <button 
                    type="button" 
                    className={styles.removeButton}
                    onClick={() => handleRemoveItem(item.id)}
                    title="Remove item"
                  >
                    <FaTrash style={{marginRight: '5px'}} /> Remove
                  </button>
                </div>
              </div>
            ))}
            
            <div className={styles.orderTotal}>
              <h5>Total:</h5>
              <p>${calculateTotal().toFixed(2)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Order Summary */}
      <div className={styles.orderSummary}>
        <h4>Order Summary</h4>
        {selectedItems.length === 0 ? (
          <p>No items selected</p>
        ) : (
          <>
            <div className={styles.selectedItemsList}>
              {selectedItems.map(item => (
                <div key={item.id} className={styles.selectedItem}>
                  <div className={styles.itemInfo}>
                    <h5>{item.name}</h5>
                    <p>${item.finalPrice.toFixed(2)} × 
                      <input 
                        type="number" 
                        min="1" 
                        value={item.quantity} 
                        onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                        className={styles.quantityInput}
                      />
                    </p>
                  </div>
                  <div className={styles.itemActions}>
                    <button 
                      type="button" 
                      className={styles.removeButton}
                      onClick={() => handleRemoveItem(item.id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            <div className={styles.orderTotals}>
              <div className={styles.totalRow}>
                <span>Total:</span>
                <span>${calculateTotal().toFixed(2)}</span>
              </div>
              
              {/* Add commission input field */}
              <div className={styles.commissionRow}>
                <label htmlFor="commission">Commission Amount ($):</label>
                <input
                  id="commission"
                  type="text"
                  value={orderCommission}
                  onChange={handleCommissionChange}
                  placeholder="Enter commission amount"
                  className={styles.commissionInput}
                />
              </div>
            </div>
            
            <button type="submit" className={styles.submitButton}>
              Create Order
            </button>
          </>
        )}
      </div>
    </form>
  );
};

export default MarketerOrderForm;
